import { useUser } from '@/providers/userProvider';
import { User } from '@/types/user';
import { grantLimits, isAdminUser, isServiceUser, linkWithin, locationLimits } from '@/lib/accessRules';

export type { GrantLimits } from '@/lib/accessRules';

export const useHasPermission = (permission: string) => {
  const { user } = useUser();

  return { hasPermission: user?.permissions.includes(permission) };
};

export const useIsAdmin = () => {
  const { user } = useUser();
  if (!user) return false;
  return user.groups.includes('Admins');
};

// Whether the current user can push fences to live carts. Wraps the
// "GeoFence Publishers" group membership check + superuser bypass.
export const useCanPublishGeofence = () => {
  const { user } = useUser();
  if (!user) return false;
  if (user.is_superuser) return true;
  return user.groups.includes('GeoFence Publishers');
};


// Who may add/edit/delete locations and regions for a customer. Mirrors the
// API (api/user_customer/access.py), which enforces it: Admins and superusers
// for every customer; Service users for customers they're linked to — fleet
// scope to add locations or manage regions, and only locations inside their
// scope to edit. Everyone else can look but not change, so the controls are
// hidden rather than left to fail with a 403.
export const useLocationAccess = (customerId: string) => {
  const { user } = useUser();
  return locationLimits(user, customerId);
};

// Who may add, edit and deactivate users. Mirrors the API
// (can_add_customer_user / can_manage_customer_user), which enforces it:
// Admins and superusers manage anyone; Service users add and manage customer
// portal users at customers they're linked to, never beyond their own access.
// Everyone else (customer portal, marketing, API users) gets no controls rather
// than a 403. Deleting an account outright is superuser-only, in the Django admin.
// `customers` supplies each customer's locations, for region-based limits.
export const useUserManagement = (
  customers?: { id: string; locations?: { id: string; region?: string | null }[] }[]
) => {
  const { user } = useUser();
  const isAdmin = isAdminUser(user);
  const isService = isServiceUser(user);

  const limitsFor = (customerId: string, locations?: { id: string; region?: string | null }[]) =>
    grantLimits(user, customerId, locations ?? customers?.find((c) => c.id === customerId)?.locations);

  const canManageUser = (target: User) => {
    if (isAdmin) return true;
    if (!isService || target.user_type !== 'customer') return false;
    // Customer Portal Users only: a Marketing or API user an Admin marked as a
    // customer stays Admin-managed (api#313).
    if (!target.groups?.includes('Customer Portal Users')) return false;
    const links = target.customers ?? [];
    return links.length > 0 && links.every((l) => linkWithin(limitsFor(l.id), l));
  };

  return {
    isAdmin,
    isService,
    canAddUsers: isAdmin || (isService && (user?.customers?.length ?? 0) > 0),
    canManageUser,
    limitsFor,
  };
};

// The same rules for one customer's Users page.
export const useCustomerUserAccess = (
  customerId: string,
  locations: { id: string; region?: string | null }[]
) => {
  const { isAdmin, isService, canManageUser, limitsFor } = useUserManagement([
    { id: customerId, locations },
  ]);
  const { user } = useUser();
  const limits = limitsFor(customerId, locations);
  const linked = !!user?.customers?.some((c) => c.id === customerId);

  return {
    canAddUsers: isAdmin || (isService && linked),
    canManageUser,
    allowFleet: limits.allowFleet,
    canGrantLocation: limits.canGrantLocation,
    canGrantRegion: limits.canGrantRegion,
  };
};

// Previewing the portal or Companion *as* another user sends
// X-Impersonate-User-Id, which the API only honours for superusers (it is
// silently ignored for anyone else, who would then see their own view under a
// "Previewing as …" banner). So only superusers get the option.
export const useCanImpersonate = () => {
  const { user } = useUser();
  return !!user?.is_superuser;
};
